import { TFunction } from "i18next";

import { TMessage, TStatus } from "../types/error";
import { BrowserType } from "../types/browser";
import { Option } from "../types/option";
import { OSType } from "../types/os";

/**
 * Returns the HTTP status code corresponding to the provided error message.
 * @param {TStatus} params.config - An error configuration object mapping HTTP status codes (as string keys) to arrays of error messages.
 * @param {string} params.type - The error message to search for.
 * @returns {number | null} The HTTP status code as a number if a matching error message is found; otherwise, null.
 */
export const getStatusCode = ({
  config,
  type,
}: {
  config: TStatus;
  type: string;
}): number => {
  for (const code in config) {
    if (config[code as keyof typeof config].includes(type)) {
      return parseInt(code, 10);
    }
  }
  return 502;
};

/**
 * Retrieves the error message corresponding to a specific id and HTTP status code from the provided configuration.
 * @param {TMessage} params.config - The array of message configurations.
 * @param {string} params.id - The unique identifier for the message item.
 * @param {number} params.code - The HTTP status code for which the error message is needed.
 * @param {"ja" | "en"} params.local - The locale specifying the language of the error message.
 * @returns {string} The error message for the given id, code, and locale. Returns an empty string if no matching message is found.
 */
export const getErrorMessage = ({
  config,
  id,
  code,
  local,
}: {
  config: TMessage;
  id: string;
  code: number;
  local: "ja" | "en";
}): string => {
  const message = config.find((item) => item.id === id);
  if (!message) return "";
  const status = message.status.find((s) => s.code === code);
  if (!status) return "";
  return status.message[local];
};

/**
 * Capitalizes the first letter of a given string while converting the rest to lowercase.
 * @param {string} string - The input string to be capitalized.
 * @returns {string} The capitalized string.
 */
export const formatUpperCase = (string: string): string => {
  if (typeof string !== "string" || !string) return string;
  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
};

/**
 * Cleans the input string by removing markdown syntax and HTML tags,
 * then normalizes whitespace by replacing multiple spaces and newlines with a single space.
 * @param input - The original string containing markdown and HTML.
 * @returns A cleaned string with markdown and HTML tags removed, and whitespace normalized.
 */
export function cleanString(input: string): string {
  let str = input.replace(/^\s*#+\s*/gm, "");
  str = str.replace(/^\s*-\s+/gm, "");
  str = str.replace(/<[^>]+>/g, "");
  str = str.replace(/\s+/g, " ").trim();
  return str;
}

export const formatUTCtoJSTWithoutTZ = (utcString: string): string => {
  const date = new Date(utcString);
  date.setHours(date.getHours() + 9);
  const pad = (num: number, size: number = 2): string => {
    let s = num.toString();
    while (s.length < size) s = "0" + s;
    return s;
  };
  return (
    date.getFullYear() +
    "-" +
    pad(date.getMonth() + 1) +
    "-" +
    pad(date.getDate()) +
    "T00" +
    ":00" +
    ":00" +
    ".000"
  );
};

/**
 * Formats a given date into a human-readable string.
 * Supports both absolute formats (e.g., "YYYY/MM/DD HH:MM") and relative formats
 * (e.g., "5 minutes ago") when `relativeThresholdMs` and `t` are provided.
 * @param date – The date to format (string, UNIX timestamp in seconds, or Date).
 * @param formatType – One of the supported format strings defined in `FormatType`.
 * @param options.relativeThresholdMs – If provided and the date is within this range (ms) from `now`,
 *   will return a relative string (requires `t`).
 * @param options.now – Reference time used for relative comparison (useful for testing).
 * @param options.t – i18n function for translating relative phrases.
 * @returns The formatted date string (or `""` for invalid inputs).
 */
export type FormatType =
  | "YYYY-MM-DD"
  | "YYYY/MM/DD"
  | "YYYY-MM-DD-HH-MM"
  | "YYYY/MM/DD HH:MM"
  | "YYYY-MM-DD-HH-MM-SS"
  | "YYYY/MM/DD HH:MM:SS"
  | "DD/MM/YYYY"
  | "YY/MM"
  | "MM/DD/YYYY"
  | "MM-DD-YYYY"
  | "MM/DD HH:MM"
  | "YYYY.MM.DD"
  | "DD.MM.YYYY"
  | "DD MMM YYYY"
  | "MMM DD, YYYY"
  | "MMMM D, YYYY"
  | "HH:MM"
  | "HH:MM:SS"
  | "hh:mm A"
  | "YYYY-MM-DDTHH:MM:SSZ";

export const formatDate = (
  date: string | number | Date | null | undefined,
  formatType: FormatType = "YYYY/MM/DD HH:MM",
  options?: {
    relativeThresholdMs?: number;
    now?: Date;
    t?: TFunction;
  }
): string => {
  if (date == null) return "";
  let parsedDate: Date;
  if (typeof date === "number") {
    parsedDate = new Date(date * 1000);
  } else if (typeof date === "string") {
    parsedDate = new Date(date);
  } else {
    parsedDate = date;
  }
  if (isNaN(parsedDate.getTime())) return "";

  const now = options?.now ?? new Date();
  const diffMs = now.getTime() - parsedDate.getTime();
  if (
    options?.relativeThresholdMs != null &&
    diffMs >= 0 &&
    diffMs < options.relativeThresholdMs &&
    options.t
  ) {
    const seconds = Math.floor(diffMs / 1000);
    if (seconds < 60) {
      return seconds < 5
        ? options.t("datetime.just_now")
        : options.t("datetime.seconds_ago", { count: seconds });
    }
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) {
      return options.t("datetime.minutes_ago", { count: minutes });
    }
    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
      return options.t("datetime.hours_ago", { count: hours });
    }
    const days = Math.floor(hours / 24);
    return options.t("datetime.days_ago", { count: days });
  }

  const y = parsedDate.getFullYear();
  const M = parsedDate.getMonth() + 1;
  const d = parsedDate.getDate();
  const h = parsedDate.getHours();
  const m = parsedDate.getMinutes();
  const s = parsedDate.getSeconds();

  const two = (n: number) => (n < 10 ? `0${n}` : `${n}`);

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const shortMonthNames = monthNames.map((n) => n.slice(0, 3));

  const hour12 = h % 12 === 0 ? 12 : h % 12;
  const ampm = h < 12 ? "AM" : "PM";

  switch (formatType) {
    case "YYYY-MM-DD":
      return `${y}-${two(M)}-${two(d)}`;
    case "YYYY/MM/DD":
      return `${y}/${two(M)}/${two(d)}`;
    case "YYYY-MM-DD-HH-MM":
      return `${y}-${two(M)}-${two(d)}-${two(h)}-${two(m)}`;
    case "YYYY/MM/DD HH:MM":
      return `${y}/${two(M)}/${two(d)} ${two(h)}:${two(m)}`;
    case "YYYY-MM-DD-HH-MM-SS":
      return `${y}-${two(M)}-${two(d)}-${two(h)}-${two(m)}-${two(s)}`;
    case "YYYY/MM/DD HH:MM:SS":
      return `${y}/${two(M)}/${two(d)} ${two(h)}:${two(m)}:${two(s)}`;
    case "DD/MM/YYYY":
      return `${two(d)}/${two(M)}/${y}`;
    case "YY/MM":
      return `${y.toString().slice(-2)}/${two(M)}`;
    case "MM/DD/YYYY":
      return `${two(M)}/${two(d)}/${y}`;
    case "MM-DD-YYYY":
      return `${two(M)}-${two(d)}-${y}`;
    case "MM/DD HH:MM":
      return `${two(M)}/${two(d)} ${two(h)}:${two(m)}`;
    case "YYYY.MM.DD":
      return `${y}.${two(M)}.${two(d)}`;
    case "DD.MM.YYYY":
      return `${two(d)}.${two(M)}.${y}`;

    case "DD MMM YYYY":
      return `${two(d)} ${shortMonthNames[M - 1]} ${y}`;
    case "MMM DD, YYYY":
      return `${shortMonthNames[M - 1]} ${two(d)}, ${y}`;
    case "MMMM D, YYYY":
      return `${monthNames[M - 1]} ${d}, ${y}`;

    case "HH:MM":
      return `${two(h)}:${two(m)}`;
    case "HH:MM:SS":
      return `${two(h)}:${two(m)}:${two(s)}`;
    case "hh:mm A":
      return `${two(hour12)}:${two(m)} ${ampm}`;

    case "YYYY-MM-DDTHH:MM:SSZ":
      return parsedDate.toISOString();

    default:
      return "";
  }
};

/**
 * Converts a number into a string formatted with commas for readability,
 * according to the specified locale.
 * @param {number} amount - The number to be formatted.
 * @param {string} locale - The locale to use for formatting.
 * @return {string} A string representing the formatted number.
 */
export const formatAmount = (
  amount: number,
  locale: string = "en-US"
): string => {
  return amount.toLocaleString(locale);
};

/**
 * This function takes a file size in bytes and converts it into a human-readable
 * format, choosing the most appropriate unit (Bytes, KB, MB, GB, TB, PB) based on the size.
 * It returns an object with separate values for the size and unit.
 * @param {number} size - The file size in bytes.
 * @return {object} An object containing the formatted file size and the unit.
 */
export const formatFileSize = (
  size: number
): { value: number; unit: string } => {
  if (size < 1024) {
    return { value: size, unit: "Bytes" };
  } else if (size < 1024 * 1024) {
    return { value: Math.floor(size / 1024), unit: "KB" };
  } else if (size < 1024 * 1024 * 1024) {
    return { value: Math.floor(size / 1024 / 1024), unit: "MB" };
  } else if (size < 1024 * 1024 * 1024 * 1024) {
    return { value: Math.floor(size / 1024 / 1024 / 1024), unit: "GB" };
  } else if (size < 1024 * 1024 * 1024 * 1024 * 1024) {
    return { value: Math.floor(size / 1024 / 1024 / 1024 / 1024), unit: "TB" };
  } else {
    return {
      value: Math.floor(size / 1024 / 1024 / 1024 / 1024 / 1024),
      unit: "PB",
    };
  }
};

/**
 * Updates the 'value' property for each Option in the data array.
 * For the first 'target' items (based on index), their 'value' is set to true.
 * Other items retain their original 'value'.
 * @param {Object} params - The function parameters.
 * @param {Option[]} params.data - An array of Option objects.
 * @param {number} params.target - The index threshold; all items with an index less than this will have their 'value' set to true.
 * @returns {Option[]} A new array of Option objects with updated 'value' properties.
 */
export const formatValue = (data: Option[], target: number): Option[] => {
  return data.map((attr, index) => ({
    ...attr,
    value: index < target ? true : attr.value,
  }));
};

/**
 * Detects the operating system of the user's device.
 * @returns {OSType} - The name of the operating system ('macOS', 'iOS', 'Windows', 'Android', 'Linux', or 'Unknown').
 */
export const detectOS = (): OSType => {
  const userAgent = window.navigator.userAgent;
  let os: OSType = "Unknown";

  if (/Macintosh|MacIntel|MacPPC|Mac68K/.test(userAgent)) {
    os = "macOS";
  } else if (/iPhone|iPad|iPod/.test(userAgent)) {
    os = "iOS";
  } else if (/Win32|Win64|Windows|WinCE/.test(userAgent)) {
    os = "Windows";
  } else if (/Android/.test(userAgent)) {
    os = "Android";
  } else if (/Linux/.test(userAgent)) {
    os = "Linux";
  }

  return os;
};

/**
 * Detects the browser of the user's device.
 * @returns {BrowserType} - The name of the browser ('Chrome', 'Firefox', 'Safari', 'Edge', 'Opera', 'Internet Explorer', or 'Unknown').
 */
export const detectBrowser = (): BrowserType => {
  const userAgent = window.navigator.userAgent;
  let browser: BrowserType = "Unknown";

  if (
    /Chrome/.test(userAgent) &&
    !/Edge/.test(userAgent) &&
    !/OPR/.test(userAgent)
  ) {
    browser = "Chrome";
  } else if (/Firefox/.test(userAgent)) {
    browser = "Firefox";
  } else if (
    /Safari/.test(userAgent) &&
    !/Chrome/.test(userAgent) &&
    !/Edge/.test(userAgent) &&
    !/OPR/.test(userAgent)
  ) {
    browser = "Safari";
  } else if (/Edge/.test(userAgent)) {
    browser = "Edge";
  } else if (/OPR/.test(userAgent) || /Opera/.test(userAgent)) {
    browser = "Opera";
  } else if (/MSIE/.test(userAgent) || /Trident/.test(userAgent)) {
    browser = "Internet Explorer";
  }

  return browser;
};

/**
 * Simulates an asynchronous operation that resolves or rejects based on the provided state.
 * @param {"success" | "error"} state - The state of the promise; "success" resolves, "error" rejects.
 * @returns {Promise<{name: string}>} A promise that resolves with an object containing a `name` property
 *                                     if the state is "success", or rejects with an error if the state is "error".
 */
export const makePromise = (state: "success" | "error") => {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      if (state === "success") {
        resolve({ name: "Sonner" });
      } else {
        reject(new Error("Mocked error"));
      }
    }, 2000);
  });
};

/**
 * Calculate an array of tick values for a given range of numbers.
 * The function rounds the input values, calculates the range with margins,
 * and then creates evenly distributed ticks within this range.
 * @param {number[]} values - The array of numbers for which to generate tick values.
 * @param {number} num_ticks - The desired number of ticks (default is 5).
 * @param {number} margin_percent - The margin percentage to apply on each side of the range (default is 0.1).
 * @param {boolean} enable_minus - Flag to allow negative tick values (default is false).
 * @returns {number[]} An array of numbers representing the tick values.
 */
export const calcChartTicks = (
  values: number[],
  num_ticks: number = 5,
  margin_percent: number = 0.1,
  enable_minus: boolean = false
): number[] => {
  // Rounded values array
  const roundedValue = values.map((num) => Math.round(num * 10) / 10);

  // Find the minimum and maximum values in the values array.
  const { min, max } = roundedValue.reduce(
    (acc, value) => ({
      min: Math.min(acc.min, value),
      max: Math.max(acc.max, value),
    }),
    { min: roundedValue[0], max: roundedValue[0] }
  );

  // Calculate the range and margins for the ticks.
  const range = max - min;
  const margin = range * margin_percent;
  let extendedMin = min - margin;
  const extendedMax = max + margin;

  // Determine the step for the tick values.
  let step;
  const base = Math.floor(Math.log10(range / num_ticks));
  const approxStep = range / (num_ticks * 10 ** base);

  if (approxStep <= 2) {
    step = 2 * 10 ** base;
  } else if (approxStep <= 5) {
    step = 5 * 10 ** base;
  } else {
    step = 10 ** (base + 1);
  }

  // Ensure step is a multiple of 0.1
  step = Math.ceil(step * 10) / 10;

  // Calculate the minimum and maximum tick values.
  let tickMin;
  if (!enable_minus && extendedMin < 0) {
    tickMin = 0;
  } else {
    tickMin = Math.floor(extendedMin / step) * step;
  }
  const tickMax = Math.ceil(extendedMax / step) * step;

  // Ensure newStep is a multiple of 0.1
  const newStep = Math.round(((tickMax - tickMin) / (num_ticks - 1)) * 10) / 10;

  // Generate the tick values and return the array.
  const ticks = [];
  for (let i = 0; i < num_ticks; i++) {
    ticks.push(Math.round((tickMin + newStep * i) * 10) / 10);
  }

  return ticks;
};
